import type { Wordart, WordartenSourceSentence, WordartenSourceToken } from "./wortarten-saetze";

export type WordartenGeneratorFamily = "basis" | "adverb" | "pronomen" | "praeposition" | "konjunktion" | "interjektion" | "numerale" | "nominalisierung" | "partikel";

interface Word {
  surface: string;
  lemma: string;
  pos: Wordart;
  nominalized?: boolean;
  after?: string;
}

interface Subject {
  article: Word;
  adjective: Word;
  noun: Word;
  verb: Word;
}

interface ObjectPhrase {
  article: Word;
  adjective: Word;
  noun: Word;
}

type Actor = Pick<Subject, "article" | "adjective" | "noun">;

const word = (surface: string, pos: Wordart, lemma: string, options: Pick<Word, "nominalized" | "after"> = {}): Word => ({ surface, pos, lemma, ...options });
const article = (surface: string, lemma = surface.toLocaleLowerCase("de-DE")) => word(surface, "Artikel", lemma);
const adjective = (surface: string, lemma: string) => word(surface, "Adjektiv", lemma);
const noun = (surface: string, lemma = surface) => word(surface, "Nomen", lemma);
const verb = (surface: string, lemma: string) => word(surface, "Verb", lemma);
const adverb = (surface: string) => word(surface, "Adverb", surface.toLocaleLowerCase("de-DE"));

function subject(art: string, adj: string, adjLemma: string, n: string, v: string, verbLemma: string): Subject {
  return { article: article(art), adjective: adjective(adj, adjLemma), noun: noun(n), verb: verb(v, verbLemma) };
}

function objectPhrase(art: string, adj: string, adjLemma: string, n: string, articleLemma = "ein", nounLemma = n): ObjectPhrase {
  return { article: article(art, articleLemma), adjective: adjective(adj, adjLemma), noun: noun(n, nounLemma) };
}

function actor(art: string, adj: string, adjLemma: string, n: string): Actor {
  return { article: article(art), adjective: adjective(adj, adjLemma), noun: noun(n) };
}

const SUBJECTS: Subject[] = [
  subject("Der", "kleine", "klein", "Hund", "rennt", "rennen"),
  subject("Die", "freundliche", "freundlich", "Lehrerin", "liest", "lesen"),
  subject("Das", "bunte", "bunt", "Kind", "malt", "malen"),
  subject("Der", "fleißige", "fleißig", "Schüler", "schreibt", "schreiben"),
  subject("Die", "alte", "alt", "Katze", "schläft", "schlafen"),
  subject("Das", "mutige", "mutig", "Mädchen", "klettert", "klettern"),
  subject("Der", "neue", "neu", "Junge", "spielt", "spielen"),
  subject("Die", "kluge", "klug", "Schülerin", "rechnet", "rechnen"),
  subject("Das", "leise", "leise", "Radio", "spielt", "spielen"),
  subject("Der", "junge", "jung", "Lehrer", "steht", "stehen"),
  subject("Die", "laute", "laut", "Glocke", "klingelt", "klingeln"),
  subject("Das", "runde", "rund", "Pausenbrot", "liegt", "liegen"),
  subject("Der", "gelbe", "gelb", "Bus", "hält", "halten"),
  subject("Die", "große", "groß", "Klasse", "übt", "üben"),
  subject("Das", "schwere", "schwer", "Buch", "liegt", "liegen"),
  subject("Der", "schnelle", "schnell", "Läufer", "trainiert", "trainieren"),
  subject("Die", "kleine", "klein", "Maus", "huscht", "huschen"),
  subject("Das", "braune", "braun", "Pferd", "läuft", "laufen"),
  subject("Der", "helle", "hell", "Stern", "leuchtet", "leuchten"),
  subject("Die", "bunte", "bunt", "Mappe", "liegt", "liegen"),
  subject("Das", "volle", "voll", "Glas", "steht", "stehen"),
  subject("Der", "rote", "rot", "Stift", "fällt", "fallen"),
  subject("Die", "schnelle", "schnell", "Ente", "schwimmt", "schwimmen"),
  subject("Das", "warme", "warm", "Licht", "scheint", "scheinen"),
  subject("Der", "volle", "voll", "Ranzen", "steht", "stehen"),
  subject("Die", "nette", "nett", "Freundin", "winkt", "winken"),
  subject("Das", "kleine", "klein", "Boot", "fährt", "fahren"),
  subject("Der", "frische", "frisch", "Apfel", "liegt", "liegen"),
  subject("Die", "neue", "neu", "Aufgabe", "steht", "stehen"),
  subject("Das", "lustige", "lustig", "Spiel", "beginnt", "beginnen"),
  subject("Der", "ruhige", "ruhig", "Schüler", "wartet", "warten"),
];

const OBJECTS: ObjectPhrase[] = [
  objectPhrase("einen", "roten", "rot", "Ball"), objectPhrase("ein", "spannendes", "spannend", "Buch"),
  objectPhrase("eine", "schöne", "schön", "Blume"), objectPhrase("einen", "gelben", "gelb", "Stift"),
  objectPhrase("eine", "leichte", "leicht", "Aufgabe"), objectPhrase("ein", "großes", "groß", "Bild"),
  objectPhrase("eine", "blaue", "blau", "Mappe"), objectPhrase("einen", "kurzen", "kurz", "Text"),
  objectPhrase("ein", "sauberes", "sauber", "Blatt"), objectPhrase("eine", "bunte", "bunt", "Karte"),
  objectPhrase("einen", "neuen", "neu", "Pinsel"), objectPhrase("eine", "kleine", "klein", "Dose"),
];

const BASE_ACTORS = SUBJECTS.filter((_, index) => [0, 1, 2, 3, 4, 5, 6, 7, 9, 25, 15, 30].includes(index));
const SIMPLE_ACTORS = [
  ["Der", "Lehrer"], ["Die", "Lehrerin"], ["Das", "Kind"], ["Der", "Schüler"], ["Die", "Schülerin"],
  ["Das", "Mädchen"], ["Der", "Junge"], ["Die", "Freundin"], ["Der", "Freund"], ["Die", "Klasse"],
].map(([art, n]) => ({ article: article(art!), noun: noun(n!) }));
const TRANSITIVE_VERBS = [
  verb("sieht", "sehen"), verb("findet", "finden"), verb("holt", "holen"), verb("ordnet", "ordnen"), verb("trägt", "tragen"),
  verb("packt", "packen"), verb("zeigt", "zeigen"), verb("braucht", "brauchen"), verb("nimmt", "nehmen"), verb("bringt", "bringen"),
];
const ADVERBS = ["schnell", "laut", "gern", "leise", "ruhig", "vorsichtig", "draußen", "heute", "sofort", "gemeinsam", "morgen", "unten", "fröhlich", "oft", "manchmal", "neugierig"].map(adverb);

const PRONOUNS = [
  ["Ich", "ich", ["suche", "male", "packe", "ordne", "hole", "zeige", "brauche", "finde"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Du", "du", ["suchst", "malst", "packst", "ordnest", "holst", "zeigst", "brauchst", "findest"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Er", "er", ["sucht", "malt", "packt", "ordnet", "holt", "zeigt", "braucht", "findet"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Sie", "sie", ["sucht", "malt", "packt", "ordnet", "holt", "zeigt", "braucht", "findet"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Wir", "wir", ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Ihr", "ihr", ["sucht", "malt", "packt", "ordnet", "holt", "zeigt", "braucht", "findet"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
  ["Sie", "sie", ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"], ["suchen", "malen", "packen", "ordnen", "holen", "zeigen", "brauchen", "finden"]],
].map(([surface, lemma, surfaces, lemmas]) => ({ pronoun: word(surface as string, "Pronomen", lemma as string), verbs: (surfaces as string[]).map((form, index) => verb(form, (lemmas as string[])[index]!)) }));

const PREPOSITION_PHRASES = [
  ["durch", "den", "Garten"], ["in", "der", "Klasse"], ["an", "dem", "Tisch"], ["auf", "dem", "Schulhof"],
  ["in", "dem", "Zimmer"], ["an", "der", "Tafel"], ["vor", "der", "Schule"], ["in", "dem", "Raum"],
  ["unter", "dem", "Tisch"], ["auf", "dem", "Sportplatz"], ["unter", "dem", "Schrank"], ["über", "die", "Wiese"],
  ["neben", "dem", "Ranzen"], ["vor", "der", "Tür"], ["über", "den", "See"], ["auf", "dem", "Blatt"],
].map(([prep, art, n]) => ({ preposition: word(prep!, "Präposition", prep!), article: article(art!, art === "die" ? "die" : "der"), noun: noun(n!) }));
const CONJUNCTIONS = ["und", "aber", "denn", "oder", "weil", "wenn"].map((value) => word(value, "Konjunktion", value));
const INTERJECTIONS = ["Hurra", "Aua", "Oh", "Hallo", "Stopp", "Prima", "Puh", "Wow", "Ups", "He"].map((value) => word(value, "Interjektion", value.toLocaleLowerCase("de-DE"), { after: ", " }));
const NUMERALS = ["Zwei", "Drei", "Vier", "Fünf", "Sechs", "Sieben", "Acht", "Viele", "Einige", "Mehrere"].map((value) => word(value, "Numerale", value.toLocaleLowerCase("de-DE")));
const NUMERAL_PHRASES = [
  ["kleine", "klein", "Hunde", "Hund", "rennen", "rennen"], ["bunte", "bunt", "Kinder", "Kind", "malen", "malen"],
  ["gelbe", "gelb", "Stifte", "Stift", "liegen", "liegen"], ["fleißige", "fleißig", "Schüler", "Schüler", "schreiben", "schreiben"],
  ["rote", "rot", "Bälle", "Ball", "rollen", "rollen"], ["neue", "neu", "Hefte", "Heft", "fehlen", "fehlen"],
  ["laute", "laut", "Kinder", "Kind", "rufen", "rufen"], ["schwere", "schwer", "Bücher", "Buch", "liegen", "liegen"],
  ["helle", "hell", "Lichter", "Licht", "leuchten", "leuchten"], ["kleine", "klein", "Boote", "Boot", "fahren", "fahren"],
  ["freundliche", "freundlich", "Lehrer", "Lehrer", "helfen", "helfen"], ["junge", "jung", "Pferde", "Pferd", "laufen", "laufen"],
].map(([adj, adjLemma, n, nounLemma, v, verbLemma]) => ({
  adjective: adjective(adj!, adjLemma!),
  noun: noun(n!, nounLemma!),
  verb: verb(v!, verbLemma!),
}));
const NOMINALIZATIONS = ["Lesen", "Rechnen", "Laufen", "Malen", "Schreiben", "Singen", "Turnen", "Lernen", "Spielen", "Zeichnen", "Üben", "Zählen"].map((value) => word(value, "Nomen", value, { nominalized: true }));
const NOMINALIZATION_ENDINGS = [
  ["macht", "machen", "große", "groß", "Freude", "Freude"], ["bringt", "bringen", "gute", "gut", "Laune", "Laune"],
  ["gibt", "geben", "neue", "neu", "Kraft", "Kraft"], ["weckt", "wecken", "großen", "groß", "Mut", "Mut"],
  ["schafft", "schaffen", "klare", "klar", "Ordnung", "Ordnung"], ["schenkt", "schenken", "frische", "frisch", "Ideen", "Idee"],
  ["bringt", "bringen", "tiefe", "tief", "Ruhe", "Ruhe"], ["weckt", "wecken", "starke", "stark", "Energie", "Energie"],
  ["schenkt", "schenken", "neue", "neu", "Hoffnung", "Hoffnung"],
].map(([v, verbLemma, adj, adjLemma, n, nounLemma]) => ({
  verb: verb(v!, verbLemma!), adjective: adjective(adj!, adjLemma!),
  noun: noun(n!, nounLemma!),
}));

const PARTICLE_ACTORS = [
  actor("Der", "ruhige", "ruhig", "Schüler"), actor("Die", "fleißige", "fleißig", "Schülerin"),
  actor("Das", "mutige", "mutig", "Mädchen"), actor("Der", "neue", "neu", "Junge"),
  actor("Die", "nette", "nett", "Freundin"), actor("Der", "junge", "jung", "Lehrer"),
  actor("Die", "freundliche", "freundlich", "Lehrerin"), actor("Das", "kluge", "klug", "Kind"),
];
const PARTICLE_OBJECTS = [
  objectPhrase("den", "roten", "rot", "Ball", "der"), objectPhrase("das", "bunte", "bunt", "Spielzeug", "das"),
  objectPhrase("die", "schwere", "schwer", "Tür", "die"), objectPhrase("das", "helle", "hell", "Licht", "das"),
  objectPhrase("den", "netten", "nett", "Nachbarn", "der", "Nachbar"), objectPhrase("die", "warme", "warm", "Jacke", "die"),
  objectPhrase("den", "vollen", "voll", "Tisch", "der"), objectPhrase("die", "wichtige", "wichtig", "Antwort", "die"),
];
const PARTICLE_TRANSITIVE = [
  ["räumt", "räumen", "auf"], ["wirft", "werfen", "weg"], ["macht", "machen", "zu"], ["schaltet", "schalten", "aus"],
  ["lädt", "laden", "ein"], ["zieht", "ziehen", "an"], ["räumt", "räumen", "ab"], ["schreibt", "schreiben", "auf"],
].map(([v, lemma, particle]) => ({ verb: verb(v!, lemma!), particle: word(particle!, "Partikel", particle!) }));
const PARTICLE_INTRANSITIVE = [
  ["steht", "stehen", "auf"], ["hört", "hören", "zu"], ["fährt", "fahren", "los"],
  ["kommt", "kommen", "an"], ["läuft", "laufen", "weg"], ["schaut", "schauen", "hin"],
].map(([v, lemma, particle]) => ({ verb: verb(v!, lemma!), particle: word(particle!, "Partikel", particle!) }));

const upper = (value: Word): Word => ({ ...value, surface: value.surface.charAt(0).toLocaleUpperCase("de-DE") + value.surface.slice(1) });
const lower = (value: Word): Word => ({ ...value, surface: value.surface.charAt(0).toLocaleLowerCase("de-DE") + value.surface.slice(1) });
const predicate = (value: Word): Word => ({ ...value, surface: value.surface.endsWith("e") ? value.surface.slice(0, -1) : value.surface });

function wordType(value: Word): WordartenSourceToken["wordType"] {
  if (value.nominalized) return "nominalized_verb";
  if (value.pos === "Nomen") return "noun";
  if (value.pos === "Verb") return "verb";
  if (value.pos === "Adjektiv") return "adjective";
  if (value.pos === "Numerale") return "quantity_word";
  if (["Artikel", "Pronomen", "Präposition", "Konjunktion", "Partikel"].includes(value.pos)) return "function_word";
  return "other";
}

function sentence(family: WordartenGeneratorFamily, index: number, words: Word[], suffix = "."): WordartenSourceSentence {
  return {
    id: `wa-v1-${family}-${String(index + 1).padStart(5, "0")}`,
    suffix,
    tokens: words.map((value, tokenIndex) => ({
      id: `w${tokenIndex + 1}`,
      surface: value.surface,
      lemma: value.lemma,
      expectedPos: value.pos,
      syntacticContext: value.nominalized ? "nominalized" : tokenIndex === 0 && value.pos !== "Artikel" && value.pos !== "Nomen" ? "capitalized_distractor" : value.pos === "Adverb" ? "adverbial_use" : "standard",
      wordType: wordType(value),
      ...(value.after ? { after: value.after } : {}),
    })),
  };
}

function buildBasis(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const value of SUBJECTS) {
    rows.push([value.article, value.adjective, value.noun, value.verb]);
    rows.push([upper(value.verb), lower(value.article), value.adjective, value.noun]);
    rows.push([value.article, value.noun, verb("ist", "sein"), predicate(value.adjective)]);
    rows.push([verb("Ist", "sein"), lower(value.article), value.noun, predicate(value.adjective)]);
  }
  for (const actor of BASE_ACTORS) for (const action of TRANSITIVE_VERBS) for (const object of OBJECTS) {
    rows.push([actor.article, actor.adjective, actor.noun, action, object.article, object.adjective, object.noun]);
    rows.push([upper(action), lower(actor.article), actor.adjective, actor.noun, object.article, object.adjective, object.noun]);
  }
  for (const actor of SIMPLE_ACTORS) for (const action of TRANSITIVE_VERBS) for (const object of OBJECTS) {
    rows.push([actor.article, actor.noun, action, object.article, object.adjective, object.noun]);
  }
  return rows.map((row, index) => sentence("basis", index, row, index < SUBJECTS.length * 4 && (index % 4 === 1 || index % 4 === 3) ? "?" : "."));
}

function buildAdverbs(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const value of SUBJECTS) for (const adv of ADVERBS) {
    rows.push([value.article, value.adjective, value.noun, value.verb, adv]);
    rows.push([upper(adv), value.verb, lower(value.article), value.adjective, value.noun]);
    rows.push([value.article, value.noun, verb("ist", "sein"), adv, predicate(value.adjective)]);
  }
  return rows.map((row, index) => sentence("adverb", index, row));
}

function buildPronouns(): WordartenSourceSentence[] {
  const rows: Array<{ words: Word[]; suffix?: string }> = [];
  for (const value of PRONOUNS) for (const action of value.verbs) for (const object of OBJECTS) {
    rows.push({ words: [value.pronoun, action, object.article, object.adjective, object.noun] });
    rows.push({ words: [upper(action), lower(value.pronoun), object.article, object.adjective, object.noun], suffix: "?" });
    for (const adv of [adverb("heute"), adverb("morgen"), adverb("oft")]) rows.push({ words: [upper(adv), action, lower(value.pronoun), object.article, object.adjective, object.noun] });
  }
  return rows.map((row, index) => sentence("pronomen", index, row.words, row.suffix));
}

function buildPrepositions(): WordartenSourceSentence[] {
  const rows: Array<{ words: Word[]; suffix?: string }> = [];
  for (const value of SUBJECTS) for (const phrase of PREPOSITION_PHRASES) {
    rows.push({ words: [value.article, value.adjective, value.noun, value.verb, phrase.preposition, phrase.article, phrase.noun] });
    rows.push({ words: [upper(phrase.preposition), phrase.article, phrase.noun, value.verb, lower(value.article), value.adjective, value.noun] });
    rows.push({ words: [upper(value.verb), lower(value.article), value.adjective, value.noun, phrase.preposition, phrase.article, phrase.noun], suffix: "?" });
  }
  return rows.map((row, index) => sentence("praeposition", index, row.words, row.suffix));
}

function buildConjunctions(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const first of SUBJECTS) for (const second of SUBJECTS) {
    if (first === second) continue;
    for (const conjunction of CONJUNCTIONS) {
      rows.push([first.article, first.adjective, first.noun, { ...first.verb, after: ", " }, conjunction, lower(second.article), second.adjective, second.noun, second.verb]);
    }
  }
  return rows.map((row, index) => sentence("konjunktion", index, row));
}

function buildInterjections(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const value of SUBJECTS) for (const interjection of INTERJECTIONS) rows.push([interjection, lower(value.article), value.adjective, value.noun, value.verb]);
  return rows.map((row, index) => sentence("interjektion", index, row, "!"));
}

function buildNumerals(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const numeral of NUMERALS) for (const phrase of NUMERAL_PHRASES) {
    rows.push([numeral, phrase.adjective, phrase.noun, phrase.verb]);
    rows.push([word("Auf", "Präposition", "auf"), article("dem", "der"), noun("Tisch"), phrase.verb, lower(numeral), phrase.adjective, phrase.noun]);
    rows.push([adverb("Heute"), phrase.verb, lower(numeral), phrase.adjective, phrase.noun]);
  }
  return rows.map((row, index) => sentence("numerale", index, row));
}

function buildNominalizations(): WordartenSourceSentence[] {
  const rows: Word[][] = [];
  for (const nominalization of NOMINALIZATIONS) for (const ending of NOMINALIZATION_ENDINGS) rows.push([article("Das"), nominalization, ending.verb, ending.adjective, ending.noun]);
  return rows.map((row, index) => sentence("nominalisierung", index, row));
}

function buildParticles(): WordartenSourceSentence[] {
  const rows: Array<{ words: Word[]; suffix?: string }> = [];
  for (const actor of PARTICLE_ACTORS) for (const action of PARTICLE_INTRANSITIVE) rows.push({ words: [actor.article, actor.adjective, actor.noun, action.verb, action.particle] });
  for (const actor of PARTICLE_ACTORS) for (const object of PARTICLE_OBJECTS) for (const action of PARTICLE_TRANSITIVE) {
    rows.push({ words: [actor.article, actor.adjective, actor.noun, action.verb, object.article, object.adjective, object.noun, action.particle] });
    rows.push({ words: [upper(action.verb), lower(actor.article), actor.adjective, actor.noun, object.article, object.adjective, object.noun, action.particle], suffix: "?" });
  }
  return rows.map((row, index) => sentence("partikel", index, row.words, row.suffix));
}

let cachedPools: Record<WordartenGeneratorFamily, WordartenSourceSentence[]> | null = null;

/** Vollständiger kombinatorischer Baukasten der V1, jetzt mit expliziten Diagnosemetadaten je Token. */
export function buildWordartenGeneratorPools(): Record<WordartenGeneratorFamily, WordartenSourceSentence[]> {
  cachedPools ??= {
    basis: buildBasis(),
    adverb: buildAdverbs(),
    pronomen: buildPronouns(),
    praeposition: buildPrepositions(),
    konjunktion: buildConjunctions(),
    interjektion: buildInterjections(),
    numerale: buildNumerals(),
    nominalisierung: buildNominalizations(),
    partikel: buildParticles(),
  };
  return cachedPools;
}

export const WORTARTEN_GENERATOR_POOL_COUNTS: Record<WordartenGeneratorFamily, number> = {
  basis: 4_204,
  adverb: 1_488,
  pronomen: 3_360,
  praeposition: 1_488,
  konjunktion: 5_580,
  interjektion: 310,
  numerale: 360,
  nominalisierung: 108,
  partikel: 1_072,
};
